import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	SwipeableDrawer,
	Fab,
	Grid,
	List,
	ListItemText,
	ListItemSecondaryAction,
	ListItem,
	Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Add } from "@mui/icons-material";
import { Global } from "@emotion/react";
import useStore from "../../store/store";
import utils from "../../utils/utils";
import style from "../../utils/style";
import accountService from "../../services/account";
import { SearchBar, LinkButton, ArrowUpChip } from "../../controls";
import NoDataFound from "../../components/NoDataFound";
import { ArrowUp, ArrowDown } from "../../components/Arrows";
import AddAccount from "../../components/AddAccount";
import PageHeading from "../../components/PageHeading";
import AccountDetails from "../../components/AccountDetails";
import Heading from "../../controls/Heading";

function Account() {
	let [keyword, setKeyword] = useState("");
	let [accounts, setAccounts] = useState([]);
	let [isOpen, setIsOpen] = useState(false);
	let [isNew, setIsNew] = useState(false);
	let [isDone, setIsDone] = useState(false);
	let [cash, setCash] = useState(0);

	const navigate = useNavigate();
	let {
		token,
		setErrorMessage,
		setIsLoading,
		isLoggedIn,
		toEdit,
		setToEdit,
		setCurrObj,
	} = useStore((state) => state);

	useEffect(() => {
		if (!isLoggedIn) navigate("/login");
		reload();
	}, [keyword]);

	const reload = () => {
		setIsLoading(true);
		accountService.reload(token, keyword).then((r) => {
			if (r.error) {
				setErrorMessage(r.error);
				setIsLoading(false);
				return;
			}

			let accounts = r.data.map((elem) => {
				let fromSum = elem.fromTransactions.reduce(
					(a, b) => a + b.amount,
					0
				);
				let toSum = elem.toTransactions.reduce(
					(a, b) => a + b.amount,
					0
				);
				toSum = elem.initial ? (toSum += elem.initial) : toSum;

				let diff = fromSum - toSum;
				return {
					...elem,
					debit: diff > 0 ? diff : 0,
					credit: diff < 0 ? diff : 0,
				};
			});
			let creditSum = 0;
			let debitSum = 0;
			accounts.map((elem) => {
				creditSum += elem.credit;
				debitSum += elem.debit;
			});
			let k = Math.abs(creditSum) - Math.abs(debitSum);
			setCash(k);
			setAccounts(accounts);
			setIsLoading(false);
			setIsDone(true);
		});
	};

	const toggleDrawer = () => setIsOpen(!isOpen);

	const Puller = styled(Box)(() => ({
		width: 30,
		height: 6,
		backgroundColor: "#ccc",
		borderRadius: 3,
		position: "absolute",
		top: 8,
		left: "calc(50% - 15px)",
	}));

	return (
		<>
			<Fab
				color="primary"
				aria-label="add"
				onClick={() => {
					setCurrObj(null);
					setIsNew(true);
					setToEdit(false);
					toggleDrawer();
				}}
				style={{ position: "fixed", right: 20, bottom: 80 }}
			>
				<Add />
			</Fab>

			<Global
				styles={{
					".MuiDrawer-root > .MuiPaper-root": {
						height: isNew ? `calc(100%)` : `calc(85%)`,
						overflow: "visible",
						borderTopLeftRadius: isNew ? 0 : 10,
						borderTopRightRadius: isNew ? 0 : 10,
					},
				}}
			/>

			<SwipeableDrawer
				anchor="bottom"
				open={isOpen}
				onClose={() => {
					setCurrObj(null);
					toggleDrawer();
				}}
				onOpen={toggleDrawer}
				swipeAreaWidth={56}
				disableSwipeToOpen={true}
				ModalProps={{
					keepMounted: true,
				}}
			>
				<Puller />

				{isNew || toEdit ? (
					<AddAccount toggleDrawer={toggleDrawer} reload={reload} />
				) : (
					<AccountDetails
						reload={reload}
						toggleDrawer={toggleDrawer}
						setIsNew={setIsNew}
					/>
				)}
			</SwipeableDrawer>

			<Grid container sx={style.container}>
				<Grid item xs={6} sx={{ mb: 1 }}>
					<PageHeading text="Accounts" />
				</Grid>

				<Grid item xs={6}>
					<LinkButton
						text="Trial Balance"
						to="/trial-balance"
						float="right"
						mt={7}
					/>
				</Grid>

				<Grid item xs={12} sx={{ mb: 1 }}>
					<Heading
						variant="p"
						text={`${utils.formatToCurrency(cash, "Rs. ")}`}
					/>
				</Grid>

				<Grid item xs={12}>
					<SearchBar keyword={keyword} setKeyword={setKeyword} />
				</Grid>

				{isDone && accounts.length > 0 && (
					<Grid item xs={12} md={12}>
						<List
							sx={{
								width: "100%",
								bgcolor: "background.paper",
								p: 0,
								m: 0,
							}}
						>
							{accounts.map((row, idx) => (
								<ListItem
									key={idx}
									onClick={() => {
										setCurrObj(row);
										setToEdit(false);
										setIsNew(false);
										toggleDrawer();
									}}
									sx={{ m: 0, p: 1, cursor: "pointer" }}
								>
									{row?.type && (
										<ListItemSecondaryAction
											sx={{ right: 1 }}
										>
											<Chip
												label={utils.capitalize(
													row?.type
												)}
												size="small"
												sx={{ fontSize: "11px" }}
											/>
										</ListItemSecondaryAction>
									)}
									<ListItemText
										onClick={() => {
											setCurrObj(row);
											setToEdit(false);
											setIsNew(false);
											toggleDrawer();
										}}
										sx={{ m: 0 }}
										primary={
											<div
												style={
													style.listItemPrimaryText
												}
											>
												{row.title}
											</div>
										}
										secondary={
											<div
												style={
													style.listItemSecondaryText
												}
											>
												{row.debit !== 0 && (
													<div
														style={{
															display: "flex",
															alignItems:
																"center",
															flexDirection:
																"row",
															gap: 5,
														}}
													>
														<ArrowUp />{" "}
														{utils.formatToCurrency(
															Math.abs(row.debit),
															"Rs. "
														)}
													</div>
												)}
												{row.credit !== 0 && (
													<div
														style={{
															display: "flex",
															alignItems:
																"center",
															flexDirection:
																"row",
															gap: 5,
														}}
													>
														<ArrowDown />{" "}
														{utils.formatToCurrency(
															Math.abs(
																row.credit
															),
															"Rs. "
														)}
													</div>
												)}
												{row.credit === 0 &&
													row.debit === 0 &&
													"Nil"}
											</div>
										}
									/>
								</ListItem>
							))}
						</List>
						{accounts.length >= 12 && <ArrowUpChip />}
					</Grid>
				)}
				{isDone && accounts.length === 0 && (
					<Grid item xs={12}>
						<NoDataFound />
					</Grid>
				)}
			</Grid>
		</>
	);
}

export default Account;
